import React from 'react'
import styled from 'styled-components'
import {VisuallyHidden} from '../Primitives'
import {InfoHint} from './InfoHint'
import {BAR_TONE_VARS, BarTrack, type BarTone} from './internal/BarTrack'
import {DEFAULT_ERROR_LABEL, EMPTY_VALUE, isBlankNode, StatValue} from './internal/StatValue'

/**
 * ComparisonBars
 *
 * Paired bars, one row per figure: a primary bar (the staffer) over a reference bar (the team
 * average), with a right-hand column for the difference. The mock's "Against the team" card
 * (`.ep-cmp`). A legend names the two bars: the primary in its tone (default `--viz-navy`), the
 * reference in `--neutral-600`.
 *
 * - Scale: each row scales to its own `max`, by default the larger of its two values (that bar is
 *   full width). Pass `max={100}` for a percentage. A value above 0 always shows at least
 *   `--space-1` of fill; a null value draws an empty track.
 * - Accessibility: the rows are a list; each row's `srText` (both values and the difference, in
 *   words) is read in place of the bars, which are hidden from assistive tech. State text beside a
 *   bar ('Couldn’t load', 'Too few teammates') is read as it is.
 * - States, by the shared precedence loading > error > text > bar (see StatValue), each for the
 *   whole list, one row, or one side of a row:
 *   - loading: a skeleton bar, and the list is marked `aria-busy`. A loading row's right-hand
 *     column is a skeleton too; a loading side leaves it to the caller;
 *   - error: 'Couldn’t load' in place of the bar. The other side still draws;
 *   - `emptyText`: text in place of the bar ('Too few teammates', 'Pick a branch to compare'), or
 *     '—' for no figure. A row's `emptyText` / `emptyReason` apply to a side that names none;
 *   - One trigger per row for the reasons: a row with a `hint`, or a row `emptyReason` a side
 *     reads, gets an InfoHint beside its label (`-info`), and every reason joins it (the row's
 *     once, a side's own after the side's name). Without either, a side's own `emptyReason` makes
 *     its text its own InfoHint trigger.
 * - The legend renders above the rows. For a legend in a card header, pass `showLegend={false}`
 *   and render `ComparisonBarsLegend` there with the same labels.
 *
 * Test ids, from the root's `data-testid` (`id`): rows `${id}-row-${row.id}`, the right-hand
 * column `${id}-difference-${row.id}`, the legend `${id}-legend`. Inside a row: its hint
 * `-info`, each side `-primary` / `-reference`, a side's bar `-primary-bar` (its fill
 * `-primary-bar-fill`) and a side's reason trigger `-primary-info`. Those share the row's prefix,
 * so count rows with an anchored pattern.
 *
 * @example
 *   <ComparisonBars data-testid="staffer-metrics-against-team"
 *     legend={{primary: 'Araceli', reference: 'Team avg', referenceHint: 'Median 72% · 12 teammates'}}
 *     rows={[
 *       {id: 'fillRatio', label: 'Fill rate', max: 100,
 *        primary: {value: 79.4}, reference: {value: 73.9},
 *        srText: 'Araceli 79.4%, team average 73.9%, 5.5 points higher',
 *        trailing: <DeltaIndicator text="5.5 pts" direction="up" sentiment="positive" />},
 *       {id: 'peoplePlaced', label: 'People placed',
 *        primary: {value: 173}, reference: {value: null, emptyText: 'Too few teammates',
 *        emptyReason: '1 teammate'}, srText: 'Araceli 173; too few teammates to compare'},
 *     ]} />
 */

export type ComparisonBarsSideKey = 'primary' | 'reference'

export interface ComparisonBarsSide {
  /** The figure its bar draws, on the row's scale. null draws an empty track. */
  value: number | null
  /** This side is not known yet: a skeleton bar. */
  loading?: boolean
  /** Truthy = this side could not be read: 'Couldn’t load' in place of its bar. */
  error?: Error | null | boolean
  /** This side's error text (default: the list's `errorLabel`). */
  errorLabel?: string
  /** Text in place of the bar, e.g. 'Too few teammates'; '—' reads as no figure. */
  emptyText?: React.ReactNode
  /** Why the side reads its `emptyText`. */
  emptyReason?: React.ReactNode
}

export interface ComparisonBarsRow {
  /** The row's key, in its test ids. */
  id: string
  label: string
  primary: ComparisonBarsSide
  reference: ComparisonBarsSide
  /** The row in words for assistive tech: both values and the difference. */
  srText: string
  /** The right-hand column: a DeltaIndicator, or text such as '7.4 h median'. */
  trailing?: React.ReactNode
  /** The scale's top. Default: the larger of the two values. */
  max?: number
  /** An InfoHint beside the label (`-info`), e.g. the figure's definition. */
  hint?: React.ReactNode
  /** The hint's accessible name (default `About ${label}`). */
  hintLabel?: string
  /** Both sides are loading, and the right-hand column with them. */
  loading?: boolean
  /** Both sides failed. */
  error?: Error | null | boolean
  /** Text in place of both bars, unless a side names its own. */
  emptyText?: React.ReactNode
  /** Why both sides read the row's `emptyText`. */
  emptyReason?: React.ReactNode
}

export interface ComparisonBarsLegendLabels {
  /** The primary bar's name, e.g. the staffer's first name. */
  primary: string
  /** The reference bar's name, e.g. 'Team avg'. */
  reference: string
  /** An InfoHint beside the primary name. */
  primaryHint?: React.ReactNode
  /** An InfoHint beside the reference name, e.g. 'Median 72% · 12 teammates'. */
  referenceHint?: React.ReactNode
}

export interface ComparisonBarsLegendProps {
  legend: ComparisonBarsLegendLabels
  /** The primary swatch's tone (default navy). */
  primaryTone?: BarTone
  /** On the legend; its items are `-primary` / `-reference`, their hints `-primary-info` / … */
  'data-testid'?: string
}

export interface ComparisonBarsProps {
  rows: ComparisonBarsRow[]
  legend: ComparisonBarsLegendLabels
  /** Render the legend above the rows (default true). */
  showLegend?: boolean
  /** The primary bar's tone (default navy). The reference bar is always `--neutral-600`. */
  primaryTone?: BarTone
  /** Every row is loading. */
  loading?: boolean
  /** Every row failed. */
  error?: Error | null | boolean
  /** The error text (default "Couldn’t load"); a side's own wins. */
  errorLabel?: string
  /** Names the list of rows. */
  'aria-label'?: string
  'data-testid'?: string
}

/** The reference bar and swatch: the team-average grey, at least 3:1 against the card. */
const REFERENCE_TONE: BarTone = 'neutral'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  font-family: var(--font-sans);
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-1) var(--space-3);
  font-size: var(--fs-caption);
  line-height: var(--lh-normal);
  color: var(--text-primary);
`

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
`

/** A square swatch, its corners just softened (mock `.ep-sw`). */
const Swatch = styled.span<{$tone: BarTone}>`
  flex-shrink: 0;
  width: var(--space-2);
  height: var(--space-2);
  border-radius: calc(var(--radius-sm) / 4);
  background-color: ${({$tone}) => BAR_TONE_VARS[$tone]};
`

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`

/** Label | bars | difference: the mock's 104px | 1fr | 96px (`.ep-cmp`). */
const Row = styled.li`
  display: grid;
  grid-template-columns: calc(var(--space-24) + var(--space-2)) minmax(0, 1fr) var(--space-24);
  column-gap: var(--space-3);
  align-items: center;
  padding: calc(var(--space-1) * 1.5) 0;
`

const RowLabel = styled.span`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
  font-size: var(--fs-caption);
  line-height: var(--lh-normal);
  color: var(--text-primary);
  overflow-wrap: anywhere;
`

const Bars = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
`

/** A side reading text in place of its bar. */
const SideText = styled.div`
  font-size: var(--fs-caption);
  line-height: var(--lh-normal);
`

const Trailing = styled.div`
  min-width: 0;
  text-align: right;
  font-size: var(--fs-caption);
  line-height: var(--lh-normal);
  font-variant-numeric: tabular-nums;
  color: var(--primary-800);
`

type SideState = 'loading' | 'error' | 'text' | 'bar'

interface ResolvedSide {
  key: ComparisonBarsSideKey
  /** The legend's name for this side. */
  name: string
  state: SideState
  side: ComparisonBarsSide
  emptyText: React.ReactNode
  emptyReason: React.ReactNode
  /** The reason is the row's, shared by both sides. */
  rowReason: boolean
}

function resolveSide(
  key: ComparisonBarsSideKey,
  side: ComparisonBarsSide,
  row: ComparisonBarsRow,
  name: string,
  listLoading: boolean,
  listError: boolean,
): ResolvedSide {
  const emptyText = isBlankNode(side.emptyText) ? row.emptyText : side.emptyText
  const ownReason = !isBlankNode(side.emptyReason)
  const emptyReason = ownReason ? side.emptyReason : row.emptyReason
  let state: SideState = 'bar'
  if (listLoading || row.loading || side.loading) state = 'loading'
  else if (listError || row.error || side.error) state = 'error'
  else if (!isBlankNode(emptyText)) state = 'text'
  return {key, name, state, side, emptyText, emptyReason, rowReason: !ownReason}
}

const hasReason = (side: ResolvedSide) => side.state === 'text' && !isBlankNode(side.emptyReason)

/**
 * The row's hint with the reasons of the sides reading text: one trigger for the whole row. The
 * row's shared reason is read once; a side's own reason is prefixed with the side's name.
 */
function hintWithReasons(hint: React.ReactNode, sides: ResolvedSide[]): React.ReactNode {
  const withReason = sides.filter(hasReason)
  if (withReason.length === 0) return hint
  const shared = withReason.find((side) => side.rowReason)
  const parts: Array<{key: string; node: React.ReactNode}> = []
  if (!isBlankNode(hint)) parts.push({key: 'hint', node: hint})
  if (shared) parts.push({key: 'row', node: shared.emptyReason})
  for (const side of withReason) {
    if (side.rowReason) continue
    parts.push({
      key: side.key,
      node: (
        <>
          {side.name}: {side.emptyReason}
        </>
      ),
    })
  }
  return parts.map((part, index) => (
    <React.Fragment key={part.key}>
      {index > 0 ? ' ' : null}
      {part.node}
    </React.Fragment>
  ))
}

export function ComparisonBarsLegend({
  legend,
  primaryTone = 'navy',
  'data-testid': testId,
}: ComparisonBarsLegendProps) {
  const items: Array<{
    key: ComparisonBarsSideKey
    name: string
    hint: React.ReactNode
    tone: BarTone
  }> = [
    {key: 'primary', name: legend.primary, hint: legend.primaryHint, tone: primaryTone},
    {key: 'reference', name: legend.reference, hint: legend.referenceHint, tone: REFERENCE_TONE},
  ]
  return (
    <Legend data-testid={testId}>
      {items.map((item) => (
        <LegendItem key={item.key} data-testid={testId ? `${testId}-${item.key}` : undefined}>
          <Swatch $tone={item.tone} aria-hidden="true" />
          {item.name}
          {isBlankNode(item.hint) ? null : (
            <InfoHint
              label={`About ${item.name}`}
              content={item.hint}
              data-testid={testId ? `${testId}-${item.key}-info` : undefined}
            />
          )}
        </LegendItem>
      ))}
    </Legend>
  )
}

export function ComparisonBars({
  rows,
  legend,
  showLegend = true,
  primaryTone = 'navy',
  loading = false,
  error,
  errorLabel = DEFAULT_ERROR_LABEL,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: ComparisonBarsProps) {
  const listError = Boolean(error)
  const resolved = rows.map((row) => {
    const sides = [
      resolveSide('primary', row.primary, row, legend.primary, loading, listError),
      resolveSide('reference', row.reference, row, legend.reference, loading, listError),
    ]
    return {row, sides}
  })
  const busy = resolved.some(({sides}) => sides.some((side) => side.state === 'loading'))

  return (
    <Root data-testid={testId}>
      {showLegend ? (
        <ComparisonBarsLegend
          legend={legend}
          primaryTone={primaryTone}
          data-testid={testId ? `${testId}-legend` : undefined}
        />
      ) : null}
      <List aria-label={ariaLabel} aria-busy={busy || undefined}>
        {resolved.map(({row, sides}) => {
          const rowTestId = testId ? `${testId}-row-${row.id}` : undefined
          const rowLoading = loading || Boolean(row.loading)
          const hintLabel = row.hintLabel ?? `About ${row.label}`
          // The row's own trigger, beside its label: its hint, or the row's `emptyReason` when a
          // side reads it. Once it exists, every side's reason joins it, so the row has one trigger.
          const rowTrigger =
            !isBlankNode(row.hint) || sides.some((side) => side.rowReason && hasReason(side))
          const values = [row.primary.value, row.reference.value].filter(
            (value): value is number => value !== null && Number.isFinite(value),
          )
          const denom = row.max ?? Math.max(0, ...values)
          const percentOf = (value: number | null): number | null =>
            value === null ? null : denom > 0 ? (value / denom) * 100 : 0

          return (
            <Row key={row.id} data-testid={rowTestId}>
              <RowLabel>
                {row.label}
                {rowTrigger ? (
                  <InfoHint
                    label={hintLabel}
                    content={hintWithReasons(row.hint, sides)}
                    data-testid={rowTestId ? `${rowTestId}-info` : undefined}
                  />
                ) : null}
              </RowLabel>
              <Bars>
                <VisuallyHidden>{row.srText}</VisuallyHidden>
                {sides.map((side) => {
                  const sideTestId = rowTestId ? `${rowTestId}-${side.key}` : undefined
                  if (side.state === 'loading' || side.state === 'bar') {
                    return (
                      <div key={side.key} aria-hidden="true" data-testid={sideTestId}>
                        <BarTrack
                          size="sm"
                          tone={side.key === 'primary' ? primaryTone : REFERENCE_TONE}
                          loading={side.state === 'loading'}
                          percent={percentOf(side.side.value)}
                          data-testid={sideTestId ? `${sideTestId}-bar` : undefined}
                        />
                      </div>
                    )
                  }
                  // A row with its own trigger carries the reason there; otherwise the text explains
                  // itself.
                  const reason = rowTrigger ? undefined : side.emptyReason
                  const reasonLabel = `${hintLabel}, ${side.name}`
                  const reasonTestId = sideTestId ? `${sideTestId}-info` : undefined
                  return (
                    <SideText key={side.key} data-testid={sideTestId}>
                      {side.state === 'error' ? (
                        <StatValue error errorLabel={side.side.errorLabel ?? errorLabel} />
                      ) : side.emptyText === EMPTY_VALUE ? (
                        <StatValue
                          value={null}
                          emptyReason={reason}
                          reasonLabel={reasonLabel}
                          reasonTestId={reasonTestId}
                        />
                      ) : (
                        <StatValue
                          pending={side.emptyText}
                          pendingReason={reason}
                          reasonLabel={reasonLabel}
                          reasonTestId={reasonTestId}
                        />
                      )}
                    </SideText>
                  )
                })}
              </Bars>
              <Trailing data-testid={testId ? `${testId}-difference-${row.id}` : undefined}>
                {rowLoading ? <StatValue loading /> : row.trailing}
              </Trailing>
            </Row>
          )
        })}
      </List>
    </Root>
  )
}
